/**
 * TournamentListModal - List and manage saved Americano tournaments
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import firebaseSyncService from '../../services/firebaseSync';
import Alert from '../common/Alert';
import Badge from '../common/Badge';

const TournamentListModal = ({ isOpen, onClose, onLoad }) => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');

  const { getCurrentUser } = useAuth();
  const { showSuccess, showError } = useToast();

  const loadTournaments = useCallback(async () => {
    const user = getCurrentUser();
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      const list = await firebaseSyncService.listUserTournaments(user.email);
      setTournaments(list);
    } catch (err) {
      const errorMsg = 'Erreur lors du chargement de la liste';
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [getCurrentUser, showError]);

  useEffect(() => {
    if (isOpen) {
      loadTournaments();
    }
  }, [isOpen, loadTournaments]);

  const handleLoadTournament = async (tournamentId) => {
    const user = getCurrentUser();
    if (!user) return;

    setLoading(true);
    try {
      const data = await firebaseSyncService.loadTournament(tournamentId, user.email);
      if (data && onLoad) {
        showSuccess(`Tournoi "${data.name || 'Sans nom'}" chargé`);
        onLoad(data);
        onClose();
      }
    } catch (err) {
      const errorMsg = 'Erreur lors du chargement du tournoi';
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTournament = async (tournamentId, tournamentName) => {
    if (!window.confirm(`Supprimer "${tournamentName}" ?\n\nCette action est irréversible.`)) return;

    const user = getCurrentUser();
    if (!user) return;

    setLoading(true);
    try {
      await firebaseSyncService.deleteTournament(tournamentId, user.email);
      showSuccess('Tournoi supprimé');
      await loadTournaments();
    } catch (err) {
      const errorMsg = 'Erreur lors de la suppression';
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateTournament = async (tournamentId) => {
    const user = getCurrentUser();
    if (!user) return;

    setLoading(true);
    try {
      await firebaseSyncService.duplicateTournament(tournamentId, user.email);
      showSuccess('Tournoi dupliqué');
      await loadTournaments();
    } catch (err) {
      const errorMsg = 'Erreur lors de la duplication';
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleStartRename = (tournament) => {
    setEditingId(tournament.id);
    setEditingName(tournament.name || '');
  };

  const handleCancelRename = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleSaveRename = async (tournamentId) => {
    const user = getCurrentUser();
    if (!user || !editingName.trim()) return;

    setLoading(true);
    try {
      await firebaseSyncService.renameTournament(tournamentId, editingName, user.email);
      showSuccess('Tournoi renommé');
      setEditingId(null);
      setEditingName('');
      await loadTournaments();
    } catch (err) {
      const errorMsg = 'Erreur lors du renommage';
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp || !timestamp.toDate) return 'Date inconnue';
    const date = timestamp.toDate();
    return date.toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ zIndex: 1050 }}>
        <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-folder me-2"></i>Mes tournois Americano
              </h5>
              <button type="button" className="btn-close" onClick={onClose} aria-label="Close" disabled={loading}></button>
            </div>

            <div className="modal-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

              {loading && tournaments.length === 0 && (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary mb-3" role="status">
                    <span className="visually-hidden">Chargement...</span>
                  </div>
                  <p className="text-muted">Chargement des tournois...</p>
                </div>
              )}

              {!loading && tournaments.length === 0 && (
                <Alert variant="info">
                  <h6 className="alert-heading">
                    <i className="bi bi-info-circle me-2"></i>Aucun tournoi sauvegardé
                  </h6>
                  <p className="mb-0">Créez votre premier tournoi pour le voir apparaître ici.</p>
                </Alert>
              )}

              {tournaments.length > 0 && (
                <div className="list-group">
                  {tournaments.map((tournament) => (
                    <div key={tournament.id} className="list-group-item list-group-item-action">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1 me-3">
                          {editingId === tournament.id ? (
                            <div className="input-group input-group-sm mb-2">
                              <input
                                type="text" className="form-control"
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveRename(tournament.id);
                                  if (e.key === 'Escape') handleCancelRename();
                                }}
                                autoFocus
                              />
                              <button type="button" className="btn btn-sm btn-success" onClick={() => handleSaveRename(tournament.id)} disabled={!editingName.trim()}>
                                <i className="bi bi-check"></i>
                              </button>
                              <button type="button" className="btn btn-sm btn-secondary" onClick={handleCancelRename}>
                                <i className="bi bi-x"></i>
                              </button>
                            </div>
                          ) : (
                            <h6 className="mb-1">
                              {tournament.name || 'Sans nom'}
                              {tournament.id === firebaseSyncService.currentTournamentId && (
                                <Badge variant="primary" className="ms-2">Actuel</Badge>
                              )}
                            </h6>
                          )}
                          <small className="text-muted">
                            <i className="bi bi-clock me-1"></i>
                            Modifié : {formatDate(tournament.updatedAt)}
                          </small>
                        </div>

                        <div className="btn-group btn-group-sm" role="group">
                          <button type="button" className="btn btn-outline-primary" onClick={() => handleLoadTournament(tournament.id)} disabled={loading} title="Charger">
                            <i className="bi bi-folder-symlink"></i>
                          </button>
                          <button type="button" className="btn btn-outline-secondary" onClick={() => handleStartRename(tournament)} disabled={loading} title="Renommer">
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button type="button" className="btn btn-outline-info" onClick={() => handleDuplicateTournament(tournament.id)} disabled={loading} title="Dupliquer">
                            <i className="bi bi-files"></i>
                          </button>
                          <button type="button" className="btn btn-outline-danger" onClick={() => handleDeleteTournament(tournament.id, tournament.name || 'Sans nom')} disabled={loading} title="Supprimer">
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>Fermer</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TournamentListModal;
